import type { FunctionComponent } from 'react'
import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { getName, getGroups, logout, getScopes } from '../../utils/auth/auth'
import ChevronDown from '../../assets/icons/chevronDown.svg'
import LogoutIcon from '../../assets/icons/logout.svg'
import UserScopeModal from './UserScopeModal'
import { Breakpoint } from '../../hooks/useBreakpoint'
import Hidden from '../Theme/Hidden'
import Icon from '../Icon/Icon'

const ESC_KEY = 'Escape'

const User = styled.button<{ active?: boolean }>`
  margin-top: 0.5rem;
  padding: 0;
  font-weight: 800;
  font-size: 1.125rem;
  line-height: 1.6;
  color: var(--dark);
  background: var(--light);
  display: inline-flex;
  flex-direction: column;
  height: 100%;
  align-items: flex-start;
  cursor: pointer;

  @media screen and (min-width: 48rem) {
    font-weight: 400;
    padding: 0.5rem 1rem 1rem 1rem;
    flex-direction: row;
    align-items: center;
  }

  ${({ active }) =>
    active &&
    css`
      color: var(--dark);
      border: none;

      svg path {
        fill: var(--dark);
      }

      @media screen and (min-width: 48rem) {
        border-top: 2px solid var(--dark);
        border-left: 2px solid var(--dark);
        border-right: 2px solid var(--dark);
      }
    `}

  &:hover,
  &:focus {
    color: var(--blue);

    svg path {
      fill: var(--blue);
    }

    ${({ active }) =>
      active &&
      css`
        color: var(--dark);
        border: none;

        svg path {
          fill: var(--dark);
        }

        @media screen and (min-width: 48rem) {
          border-top: 2px solid var(--dark);
          border-left: 2px solid var(--dark);
          border-right: 2px solid var(--dark);
        }
      `}
  }
`

const UserName = styled.p`
  margin: 0 0.5rem 0 0;
  white-space: nowrap;

  &.username {
    font-size: 1rem;
  }

  &.large-username {
    font-size: 1.125rem;
    font-weight: 800;
  }

  @media screen and (min-width: 48rem) {
    &.username {
      font-size: 1.125rem;
    }
  }
`

const UserGroup = styled.p`
  font-size: 0.75rem;
  font-weight: 400;
  white-space: nowrap;
  margin: 0 1rem 0 0;
  color: var(--greyMedium);

  @media screen and (min-width: 48rem) {
    font-size: 1rem;
  }
`

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--light);
  position: relative;
  top: 0;
  right: 0;
  border: none;

  @media screen and (min-width: 48rem) {
    position: absolute;
    top: 3.125rem;
    border: 2px solid var(--dark);
  }
`

const LogoutButton = styled.button`
  background: var(--light);
  color: var(--blue);
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.6;
  padding: 0.5rem 0;
  display: flex;
  align-items: center;

  svg {
    width: 1rem;
    margin-right: 0.5rem;
    fill: var(--blue);
  }

  &:hover,
  &:focus {
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 0.25rem;
  }

  @media screen and (min-width: 48rem) {
    color: var(--dark);
    font-weight: 400;
    padding: 0.625rem 1.5rem;

    svg {
      fill: var(--dark);
    }
  }
`

const ScopeButton = styled.button`
  color: var(--light);
  background: var(--dark);
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.6;
  white-space: nowrap;
  padding: 0;
  text-decoration: underline;
  text-align: left;

  &:hover,
  &:focus {
    text-decoration-thickness: 2px;
  }

  @media screen and (min-width: 48rem) {
    font-size: 1rem;
  }
`

const UserNameScope = styled.div`
  background: var(--dark);
  color: var(--light);
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.6;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  margin: 0.5rem -1rem 0;

  svg {
    fill: var(--light);
  }

  @media screen and (min-width: 48rem) {
    margin: 0;
    padding: 0.625rem 1.5rem;
  }
`

function dropFocus() {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur()
  }
}

interface LogoutProps {
  isExpanded: boolean
  toggleMenu: (isExpanded: boolean) => void
}

const Logout: FunctionComponent<LogoutProps> = ({ isExpanded, toggleMenu }) => {
  const [openUserScopeModal, setOpenUserScopeModal] = useState(false)
  const userName = getName()
  const userGroups = getGroups()
  const userScopes = getScopes(true)

  const userGroupText = () => {
    const firstUserGroup: string = userGroups[0]?.replace('/', '') || ''
    const string = `Toegang tot: ${firstUserGroup}`

    if (userGroups.length === 1) {
      return string
    }
    if (userGroups.length === 2) {
      return `${string} en 1 andere rol`
    }
    if (userGroups.length > 2) {
      return `${string} en ${userGroups.length - 1} andere rollen`
    }
  }

  function handleLogout() {
    logout()
  }

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      const { code } = event
      if (isExpanded && code === ESC_KEY) {
        toggleMenu(false)
      }
    }

    document.addEventListener('keydown', handleKeyPress)

    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [isExpanded])

  return (
    <>
      <Hidden maxBreakpoint={Breakpoint.Md}>
        <User
          type="button"
          id="user-menu"
          onClick={() => {
            toggleMenu(!isExpanded)
          }}
          active={isExpanded}
          aria-controls="user-details"
          aria-expanded={isExpanded}
        >
          <UserName>{userName}</UserName>
          <Icon size={16} rotate={isExpanded ? 180 : 0}>
            <ChevronDown />
          </Icon>
        </User>
      </Hidden>
      {isExpanded && (
        <UserDetails id="user-details" aria-labelledby="user-menu">
          <Hidden minBreakpoint={Breakpoint.Md}>
            <UserName className="large-username">{userName}</UserName>
          </Hidden>
          <UserNameScope>
            <UserName className="username">{userName}</UserName>
            {userGroups.length > 0 && <UserGroup>{userGroupText()}</UserGroup>}
            <ScopeButton onClick={() => setOpenUserScopeModal(true)}>
              Bekijk de volledige scope van {userName}
            </ScopeButton>
            <UserScopeModal
              userName={userName}
              userGroups={userGroups}
              userScopes={userScopes}
              setOpen={setOpenUserScopeModal}
              open={openUserScopeModal}
            />
          </UserNameScope>
          <LogoutButton
            type="button"
            onClick={() => {
              handleLogout()
              dropFocus()
            }}
            data-testid="headerMenuLinkLogout"
          >
            <LogoutIcon />
            <span>Uitloggen</span>
          </LogoutButton>
        </UserDetails>
      )}
    </>
  )
}

export default Logout
